import type { DataGridColumn, PreviewImage } from "@/components/kit";

import { Button, Chip } from "@heroui/react";
import { Eye, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { Category } from "../types";

import { AddCategoryModal } from "@/components/categories/add-category";
import { EditCategoryModal } from "@/components/categories/edit-category";
import { ViewCategoryModal } from "@/components/categories/view-category";
import {
  DataTable,
  ImagePreviewModal,
  ImageTile,
  RowMenu,
  SearchBox,
  useClientTable,
} from "@/components/kit";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { AppPage } from "@/layouts/page";

export default function CategoriesPage() {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewImage | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: categories,
    isLoading,
    refetch: refetchCategories,
  } = useGetAllCategories();

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();

    if (!categories) return [];
    if (!term) return categories;

    return categories.filter(
      (category) =>
        category.name.toLowerCase().includes(term) ||
        category.nameAr?.toLowerCase().includes(term) ||
        category.id.toLowerCase().includes(term),
    );
  }, [categories, searchTerm]);

  const columns = useMemo<DataGridColumn<Category>[]>(
    () => [
      {
        id: "name",
        header: "Category",
        accessorKey: "name",
        allowsSorting: true,
        isRowHeader: true,
        cell: (category) => (
          <div className="flex items-center gap-3">
            {/* A mouse shortcut to the full image: not a tab stop, so the row's name stays the category's. */}
            <div
              className="cursor-pointer"
              onClick={() =>
                setPreview({ url: category.image, name: category.name })
              }
            >
              <ImageTile size="sm" src={category.image} />
            </div>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-medium">
                {category.name}
              </span>
              <span className="text-muted self-start truncate text-xs" dir="rtl">
                {category.nameAr || "-"}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "subCategories",
        header: "Subcategories",
        width: 150,
        allowsSorting: true,
        sortFn: (a, b) =>
          (a.subCategories?.length ?? 0) - (b.subCategories?.length ?? 0),
        cell: (category) => (
          <span className="tabular-nums">
            {category.subCategories?.length || 0}
          </span>
        ),
      },
      {
        id: "visibility",
        header: "Visibility",
        width: 140,
        allowsSorting: true,
        sortFn: (a, b) => Number(a.isHidden) - Number(b.isHidden),
        cell: (category) => (
          <Chip
            color={category.isHidden ? "default" : "success"}
            variant="soft"
          >
            {category.isHidden ? "Hidden" : "Visible"}
          </Chip>
        ),
      },
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
        width: 200,
        cell: (category) => (
          <span className="text-muted block truncate font-mono text-xs">
            {category.id}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (category) => (
          <RowMenu
            items={[
              {
                id: "view",
                label: "View details",
                icon: <Eye className="size-4" />,
                onAction: () => setViewingId(category.id),
              },
              {
                id: "edit",
                label: "Edit",
                icon: <Pencil className="size-4" />,
                onAction: () => setEditingId(category.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  const table = useClientTable(filtered, { columns });
  const find = (id: string | null) =>
    categories?.find((category) => category.id === id) ?? null;

  return (
    <AppPage
      action={
        <Button onPress={() => setIsAddOpen(true)}>
          <Plus className="size-4" />
          Add Category
        </Button>
      }
      subtitle={
        categories
          ? `${categories.length} categories and their subcategories`
          : "Manage product categories and subcategories"
      }
      title="Categories"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <SearchBox
          placeholder="Search categories"
          value={searchTerm}
          onChange={setSearchTerm}
        />
        <DataTable
          compact
          columns={columns}
          empty={{
            title: searchTerm ? "No categories match" : "No categories yet",
            description: searchTerm
              ? "Try a different name or ID."
              : "Add the first one to get started.",
            action: searchTerm ? (
              <Button
                size="sm"
                variant="secondary"
                onPress={() => setSearchTerm("")}
              >
                Clear search
              </Button>
            ) : undefined,
          }}
          getRowId={(category) => category.id}
          isLoading={isLoading}
          label="Categories"
          pagination={table.pagination}
          rows={table.rows}
          sort={table.sort}
          onSortChange={table.onSortChange}
        />
      </div>

      <ImagePreviewModal
        image={preview}
        title="Category Image"
        onClose={() => setPreview(null)}
      />

      <AddCategoryModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={() => {
          refetchCategories();
          setIsAddOpen(false);
        }}
      />

      <ViewCategoryModal
        category={find(viewingId)}
        isOpen={!!viewingId}
        onClose={() => setViewingId(null)}
        onRefresh={refetchCategories}
      />

      <EditCategoryModal
        category={find(editingId)}
        isOpen={!!editingId}
        onClose={() => setEditingId(null)}
        onSuccess={() => {
          refetchCategories();
          setEditingId(null);
        }}
      />
    </AppPage>
  );
}
